/**
 * ClientDocumentsBlock — the intake drawer's Documents section.
 *
 * Founder report 2026-07-25: "The document attachment function is showing
 * up in talent tracker and not intake. And then IDEALLY once a client is
 * converted, those documents show up their client record document tab."
 *
 * ============================================================
 * WHY THIS READS client_documents AND NOT work_item_attachments
 * ============================================================
 * The intake board's card-attachment surface is fenced OFF by seeded
 * default (0162 / ADR-0048, `work_processes.attachments_enabled = false`
 * on the is_system intake process). That fence is founder-required and
 * this file does NOT touch it. The reason it exists: intake cards name
 * prospective clients, and their Client Setup checklist collects the
 * Service Agreement, HIPAA acknowledgement, NCRS and Transportation
 * Consent. NCRS is a clinical assessment. `work_item_attachments` is
 * deliberately TENANT-tier — plaintext filename, no care-team RLS, no
 * phi_access_log emission — so filing that paperwork there is exactly
 * what the fence prevents.
 *
 * So instead of flipping the fence, this section reads and writes the
 * client's REAL documents through the shipped 0131 machinery
 * (`client_documents` + documentsApi). Three things fall out for free
 * rather than needing to be built:
 *
 *   - The founder's "show up on the client record document tab"
 *     requirement is satisfied BY CONSTRUCTION. Same rows, same store,
 *     same blob container, same shared cache key (clientDocumentsKey), so
 *     an upload here appears on the record tab and vice versa. There is
 *     no conversion step, no blob copy, no promotion pipeline to get
 *     wrong.
 *   - Encryption at rest and care-team RLS come from the existing table.
 *   - Every read is written to phi_access_log by the existing repo
 *     (withClinicalRead), so surfacing documents in a second place does
 *     not create an unaudited path to them.
 *
 * DO NOT "unify" this with AttachmentsBlock. See that file's header for
 * the full reasoning: two stores, two tiers, on purpose.
 *
 * ============================================================
 * THE CAPABILITY GATE (this is the load-bearing part)
 * ============================================================
 * The intake drawer is visible to a wider audience (view_workspace) than
 * the client record page, so surfacing client documents here could in
 * principle WIDEN who sees PHI-bearing files. It does not, because this
 * section gates on the SAME capabilities the client Documents tab and its
 * handlers use — never on the drawer's own view_workspace/manage_work_items:
 *
 *   read  → read_clinical_record   (GET /clients/{id}/documents,
 *                                   POST /documents/{id}/download)
 *   write → write_clinical_notes   (POST create/complete, DELETE)
 *
 * Measured against the shipped matrix, every role holding view_workspace
 * also holds read_clinical_record, so the read delta is EMPTY — this adds
 * zero viewers. write_clinical_notes is the one real difference:
 * board_observer opens the drawer and reads, but gets NO upload or delete
 * affordance (0152's "read everything, write only the demo client").
 * Gating writes on the clinical capability rather than manage_work_items
 * matters: manage_work_items happens to exclude board_observer today, so
 * it would look correct while being correct only by coincidence.
 *
 * Absent, never greyed: a viewer without read_clinical_record gets no
 * section at all, so the UI never leaks that a client has documents. The
 * client check is DISPLAY ONLY — the server's requireCapability is the
 * boundary, and it is re-checked on every read and every write.
 *
 * ============================================================
 * PRE-CLIENT-SETUP (client_id IS NULL)
 * ============================================================
 * `work_items.client_id` stays NULL until Client Setup creates the
 * `clients` row, and there is no clinical store to write into before that
 * exists. So the section renders NO uploader — just one quiet line, plus
 * the prospect folder when the card carries one. That URL is the SAME
 * value the drawer already derives for its "Open Client Prospect Folder"
 * link (#618/#612: the first allows_link checklist item by sort_order),
 * passed in as a prop — one derivation, one mechanism, no second link
 * substrate invented here.
 *
 * WHAT DELIBERATELY STAYS ON THE RECORD TAB. Replace/version chains and
 * the "removed earlier" archived-trove review are record-keeping chores,
 * not intake work; the drawer stays lean and the record tab remains the
 * full filing cabinet.
 */

import { useRef, useState, type DragEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DOCUMENT_ACCEPT,
  DOCUMENT_KIND_LABELS,
  DOCUMENT_KIND_OPTIONS,
  DOCUMENT_MAX_BYTES,
  clientDocumentsKey,
  documentsApi,
  isPreviewable,
  resolveContentType,
  type DocumentKind,
  type DocumentWire,
  type DocumentsListResponse,
} from '@/lib/documents';
import { openBlankViewerTab, pointViewerAt, saveGrantToDisk } from '@/lib/documentGrants';
import { Button } from '@/components/primitives/Button';
import { ConfirmDialog } from '@/components/workspace/ConfirmDialog';
import { TextAction, DestructiveTextAction } from '@/components/workspace/TextAction';
import { DrawerSection } from './drawerLayout';
import { shortDate } from '@/components/workspace/workspaceFormat';
import { WORKSPACE_DRAWER_CLIENT_DOCUMENTS_KEY } from '@/lib/keptAcrossSignOut';
import { SelectField } from '@/components/primitives/SelectField';
import { contactSentence } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';

/** The upload's retry line; `{contact_system}` is filled in at render. */
const UPLOAD_FAILED_COPY = "That file didn't upload. Add it again, and if it fails again, tell {contact_system}.";

/** The open or download failure line (house failure copy). */
const GRANT_FAILED_COPY =
  "That file didn't open. Try again, and if it happens again, tell {contact_system}.";

/** What the picker will not take, said in file terms and not mime terms. */
const TYPE_REJECTED_COPY =
  "That kind of file can't be added. A PDF, Word document, image or plain text file will go right in.";

const SIZE_REJECTED_COPY = 'That file is over the 25 MB limit.';

/** Quiet size label (a fact for the meta line). */
function formatDocumentSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

export interface ClientDocumentsBlockProps {
  tenantSlug: string;
  /**
   * The converted client, or null pre-Client-Setup. Null renders the
   * explanatory line and no uploader — there is no clinical store to
   * write into before the client row exists.
   */
  clientId: string | null;
  /**
   * read_clinical_record — mirrors the server gate on the document list
   * and the download grant. False renders NOTHING (absent, not greyed).
   */
  canReadDocuments: boolean;
  /**
   * write_clinical_notes — mirrors the server gate on upload + delete.
   * Deliberately NOT manage_work_items (see the header).
   */
  canFileDocuments: boolean;
  /**
   * The card's prospect-folder URL when it has one — the SAME value the
   * drawer derives for its "Open Client Prospect Folder" link, not a
   * second mechanism. Only used in the pre-Client-Setup state.
   */
  folderLinkUrl: string | null;
}

export function ClientDocumentsBlock({
  tenantSlug,
  clientId,
  canReadDocuments,
  canFileDocuments,
  folderLinkUrl,
}: ClientDocumentsBlockProps): ReactNode {
  const contact = useSupportContact({ enabled: false });
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [kind, setKind] = useState<DocumentKind>('other');
  /** The last attempted upload, retained so retry is one click. */
  const [lastAttempt, setLastAttempt] = useState<{ file: File; kind: DocumentKind } | null>(null);
  /** A client-side rejection, surfaced BEFORE any byte leaves the browser. */
  const [pickError, setPickError] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<DocumentWire | null>(null);

  // THE shared key: the record page's Documents tab reads the same rows
  // under the same key, so either surface refreshes the other.
  const queryKey = clientDocumentsKey(tenantSlug, clientId ?? '');
  const listQuery = useQuery<DocumentsListResponse>({
    queryKey,
    queryFn: ({ signal }) => documentsApi.list(tenantSlug, clientId as string, signal),
    // No client row and no capability = no request. An out-of-scope
    // viewer never even asks the audited endpoint.
    enabled: canReadDocuments && clientId !== null,
  });
  const rows = listQuery.data?.rows ?? [];

  const uploadMut = useMutation({
    mutationFn: (attempt: { file: File; kind: DocumentKind }) =>
      documentsApi.uploadDocument(tenantSlug, clientId as string, {
        kind: attempt.kind,
        file: attempt.file,
      }),
    onSuccess: () => {
      setLastAttempt(null);
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const removeMut = useMutation({
    mutationFn: (documentId: string) => documentsApi.deleteDocument(tenantSlug, documentId),
    onSuccess: () => {
      setRemoveTarget(null);
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const downloadMut = useMutation({
    mutationFn: (documentId: string) => documentsApi.downloadGrant(tenantSlug, documentId),
    onSuccess: (grant) => {
      saveGrantToDisk(grant.download_url);
    },
  });

  const openMut = useMutation({
    mutationFn: (vars: { documentId: string; viewer: Window | null }) =>
      documentsApi.downloadGrant(tenantSlug, vars.documentId, 'inline'),
    onSuccess: (grant, vars) => {
      pointViewerAt(vars.viewer, grant.download_url);
    },
    onError: (_err, vars) => {
      // Never strand a blank tab behind a failed grant.
      vars.viewer?.close();
    },
  });

  function openDocument(documentId: string): void {
    // Synchronous open on the gesture, pointed at the grant when it
    // lands (see documentGrants.ts).
    openMut.mutate({ documentId, viewer: openBlankViewerTab() });
  }

  /** Client-side policy first: the user hears no before anything uploads. */
  function startUpload(file: File): void {
    setPickError(null);
    if (resolveContentType(file) === null) {
      setPickError(TYPE_REJECTED_COPY);
      return;
    }
    if (file.size <= 0 || file.size > DOCUMENT_MAX_BYTES) {
      setPickError(SIZE_REJECTED_COPY);
      return;
    }
    const attempt = { file, kind };
    setLastAttempt(attempt);
    uploadMut.mutate(attempt);
  }

  function onDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    if (!canFileDocuments || uploadMut.isPending) return;
    const file = e.dataTransfer.files?.[0];
    if (file) startUpload(file);
  }

  // THE GATE. No read capability = no section, so the drawer never
  // discloses that this client has documents at all. Display mirror of
  // the server gate, which is re-checked on every request.
  if (!canReadDocuments) return null;

  // Pre-Client-Setup: the clinical store has nowhere to put a file yet.
  // One quiet line, no uploader, and the prospect folder if the card
  // carries one (the drawer's own derived URL, passed in).
  if (clientId === null) {
    return (
      <DrawerSection
        title="Documents"
        testId="drawer-client-documents"
        collapsible
        defaultOpen={false}
        storageKey={WORKSPACE_DRAWER_CLIENT_DOCUMENTS_KEY}
      >
        <p className="text-[12.5px] text-ink-soft" data-testid="client-documents-no-client">
          Documents file to the client&rsquo;s record, which starts at Client Setup. Once
          this intake has a client record, paperwork uploaded here lands on it.
        </p>
        {folderLinkUrl !== null && (
          <p className="mt-1.5 text-[12.5px] text-ink-soft">
            Until then, the{' '}
            <a
              href={folderLinkUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="client-documents-folder-link"
              className="text-sage-800 underline decoration-dotted underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded"
            >
              client prospect folder
            </a>{' '}
            holds this prospect&rsquo;s paperwork.
          </p>
        )}
      </DrawerSection>
    );
  }

  const showUploadError = !uploadMut.isPending && (uploadMut.isError || pickError !== null);

  return (
    <>
      <DrawerSection
        title="Documents"
        testId="drawer-client-documents"
        collapsible
        defaultOpen={false}
        storageKey={WORKSPACE_DRAWER_CLIENT_DOCUMENTS_KEY}
        meta={
          rows.length > 0
            ? rows.length === 1
              ? '1 document'
              : `${rows.length} documents`
            : undefined
        }
      >
        {canFileDocuments && (
          <div
            data-testid="client-documents-upload-zone"
            className="rounded-card border border-dashed border-sage-300 bg-cream/50 px-4 py-3 text-center mb-3"
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
          >
            <input
              ref={fileInputRef}
              data-testid="client-documents-upload-input"
              type="file"
              accept={DOCUMENT_ACCEPT}
              className="sr-only"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) startUpload(file);
              }}
            />
            {uploadMut.isPending ? (
              <p
                data-testid="client-documents-uploading"
                className="text-[12.5px] text-ink"
                role="status"
              >
                Uploading…
              </p>
            ) : (
              <>
                <div className="flex items-center justify-center gap-1.5 mb-2">
                  <label
                    htmlFor="client-documents-kind-select"
                    className="text-[12.5px] text-ink-soft"
                  >
                    File as
                  </label>
                  <SelectField size="compact"
                    id="client-documents-kind-select"
                    data-testid="client-documents-kind-select"
                    value={kind}
                    onChange={(e) => setKind(e.target.value as DocumentKind)}
                  >
                    {DOCUMENT_KIND_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </SelectField>
                </div>
                <p className="text-[12.5px] text-ink-soft mb-2">Drag a file here, or</p>
                <Button
                  variant="secondary"
                  size="sm"
                  data-testid="client-documents-upload-button"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Upload a document
                </Button>
              </>
            )}
            {showUploadError && (
              <p
                role="alert"
                data-testid="client-documents-upload-error"
                className="text-[11.5px] text-pepper-deep mt-2"
              >
                {pickError ?? contactSentence(UPLOAD_FAILED_COPY, contact)}{' '}
                {pickError === null && lastAttempt && (
                  <TextAction
                    data-testid="client-documents-upload-retry"
                    onClick={() => uploadMut.mutate(lastAttempt)}
                  >
                    Try again
                  </TextAction>
                )}
              </p>
            )}
          </div>
        )}

        {listQuery.isError ? (
          <p
            role="alert"
            data-testid="client-documents-list-error"
            className="text-[12.5px] text-pepper-deep"
          >
            This client&rsquo;s documents did not load.{' '}
            <TextAction onClick={() => void listQuery.refetch()}>Try again</TextAction>
          </p>
        ) : rows.length === 0 ? (
          <p data-testid="client-documents-empty" className="text-[12.5px] text-ink-soft">
            No documents on this client&rsquo;s record yet. The Service Agreement, HIPAA
            acknowledgement and the rest of the paperwork collected at Client Setup belong
            here, filed as Intake.
          </p>
        ) : (
          <ul className="divide-y divide-sage-800/[6%]" data-testid="client-documents-list">
            {rows.map((doc) => (
              <li
                key={doc.id}
                data-testid={`client-document-row-${doc.id}`}
                className="flex flex-col gap-0.5 py-2"
              >
                <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                  <span className="text-[12.5px] font-medium text-ink break-all min-w-0 flex-1">
                    {doc.display_name}
                  </span>
                  {/* One primary per row, chosen by the STORED type: the
                      previewable set opens inline, docx downloads. The
                      server coerces the disposition either way. */}
                  {isPreviewable(doc.content_type) ? (
                    <TextAction
                      data-testid={`client-document-open-${doc.id}`}
                      disabled={openMut.isPending && openMut.variables?.documentId === doc.id}
                      onClick={() => openDocument(doc.id)}
                    >
                      Open
                    </TextAction>
                  ) : (
                    <TextAction
                      data-testid={`client-document-download-${doc.id}`}
                      disabled={downloadMut.isPending && downloadMut.variables === doc.id}
                      onClick={() => downloadMut.mutate(doc.id)}
                    >
                      Download
                    </TextAction>
                  )}
                  {canFileDocuments && (
                    <DestructiveTextAction
                      data-testid={`client-document-delete-${doc.id}`}
                      onClick={() => setRemoveTarget(doc)}
                    >
                      Delete
                    </DestructiveTextAction>
                  )}
                </div>
                <p className="text-[11.5px] text-sage-400">
                  {DOCUMENT_KIND_LABELS[doc.kind] ?? 'Other'} ·{' '}
                  {doc.uploaded_by_display_name ? `${doc.uploaded_by_display_name} · ` : ''}
                  {shortDate(doc.created_at)} · {formatDocumentSize(doc.size_bytes)}
                </p>
              </li>
            ))}
          </ul>
        )}

        {(downloadMut.isError || openMut.isError) && (
          <p
            role="alert"
            data-testid="client-documents-grant-error"
            className="text-[11.5px] text-pepper-deep mt-2"
          >
            {contactSentence(GRANT_FAILED_COPY, contact)}
          </p>
        )}
      </DrawerSection>

      {/* True deletion (0141): the row and the stored file both go, for
          everyone. Same semantics as the record tab's Delete — the copy
          must never imply the file can come back. */}
      {removeTarget !== null && (
        <ConfirmDialog
          title="Delete this document?"
          body={`"${removeTarget.display_name}" leaves this client's record for everyone, and the file itself is deleted. This can't be undone.`}
          confirmLabel="Delete"
          destructive
          onCancel={() => setRemoveTarget(null)}
          onConfirm={() => removeMut.mutate(removeTarget.id)}
        />
      )}
    </>
  );
}
